import { call, put, select } from 'redux-saga/effects'
import { BE_ERROR_CODES, language_w, outcome_w } from 'src/backend-codes'
import {
  http_check_registration_data,
  http_remove_contacts,
  http_resolve_access,
  http_resolve_addresses,
  http_resolve_associated_customers,
  http_resolve_caps,
  http_resolve_check_pension_fund_seda,
  http_resolve_check_seda,
  http_resolve_consents,
  http_resolve_contacts,
  http_resolve_customer,
  http_resolve_districts,
  http_resolve_language,
  http_resolve_localities,
  http_resolve_municipalities,
  http_resolve_nations,
  http_resolve_toponyms,
  http_update_access,
  http_update_consents,
  http_update_customer,
  http_update_language,
  http_verify_registration_otp,
} from 'src/http'
import {
  addr_t,
  cap_t,
  check_registration_data_outcome_t,
  consent_t,
  contact_t,
  customer_outcome_t,
  customer_t,
  district_t,
  error_t,
  locality_t,
  municipality_t,
  nation_t,
  saga_act,
  seda_check_outcome_t,
  seda_check_t,
  verify_registration_otp_outcome_t,
} from 'src/types'

import { shoot } from '../actions'
import { select_error } from '../store'
import { saga_do_request } from './base.saga'

/**
 * Resolve customer
 */
export function* saga_resolve_customer(action: saga_act): unknown {
  const customer: customer_t = yield call(saga_do_request, http_resolve_customer, action)
  if (customer) {
    yield put(shoot.reduce_customer(customer))
  }
}

/**
 * Update customer
 */
export function* saga_update_customer(action: saga_act): unknown {
  const co: customer_outcome_t = yield call(saga_do_request, http_update_customer, action)
  if (co?.outcome === 'success') {
    yield put(shoot.reduce_outcome_customer(co))
  }
}

/**
 * Resolve contacts
 */
export function* saga_resolve_contacts(action: saga_act): unknown {
  const contacts: contact_t[] = yield call(saga_do_request, http_resolve_contacts, action)
  if (contacts) {
    yield put(shoot.reduce_contacts(contacts))
  }
}

/**
 * Remove contacts
 */
export function* saga_remove_contacts(action: saga_act): unknown {
  const outcome: outcome_w = yield call(saga_do_request, http_remove_contacts, action)
  if (outcome === 'success') {
    yield put(shoot.reduce_outcome_contacts(outcome))
  }
}

/**
 * Resolve consents
 */
export function* saga_resolve_consents(action: saga_act): unknown {
  const consents: consent_t[] = yield call(saga_do_request, http_resolve_consents, action)
  if (consents) {
    yield put(shoot.reduce_consents(consents))
  }
}

/**
 * Update consents
 */
export function* saga_update_consents(action: saga_act): unknown {
  const outcome: outcome_w = yield call(saga_do_request, http_update_consents, action)
  if (outcome === 'success') {
    yield put(shoot.reduce_outcome_consents(outcome))
  }
}

/**
 * Resolve language
 */
export function* saga_resolve_language(action: saga_act): unknown {
  const language: language_w = yield call(saga_do_request, http_resolve_language, action)
  if (language) {
    yield put(shoot.reduce_language(language))
  } else {
    const error: error_t = yield select(select_error())
    if (error?.code === BE_ERROR_CODES.LANGUAGE_NOT_FOUND) {
      yield put(shoot.reduce_error())
      yield put(shoot.reduce_language())
    }
  }
}

/**
 * Update language
 */
export function* saga_update_language(action: saga_act): unknown {
  const outcome: outcome_w = yield call(saga_do_request, http_update_language, action)
  if (outcome === 'success') {
    yield put(shoot.reduce_outcome_language(outcome))
  }
}

/**
 * Resolve access
 */
export function* saga_resolve_access(action: saga_act): unknown {
  const access: string = yield call(saga_do_request, http_resolve_access, action)
  if (access) {
    yield put(shoot.reduce_access(access))
  }
}

/**
 * Update access
 */
export function* saga_update_access(action: saga_act): unknown {
  const outcome: outcome_w = yield call(saga_do_request, http_update_access, action)
  if (outcome === 'success') {
    yield put(shoot.reduce_outcome_access(outcome))
  }
}

/**
 * Resolve nations
 */
export function* saga_resolve_nations(action: saga_act): unknown {
  const nations: nation_t[] | nation_t = yield call(saga_do_request, http_resolve_nations, action)
  if (nations) {
    if (Array.isArray(nations)) {
      yield put(shoot.reduce_nations(nations))
    } else {
      yield put(shoot.reduce_nations([nations]))
    }
  }
}

/**
 * Resolve districts
 */
export function* saga_resolve_districts(action: saga_act): unknown {
  const districts: district_t[] = yield call(saga_do_request, http_resolve_districts, action)
  if (districts) {
    yield put(shoot.reduce_districts(districts))
  }
}

/**
 * Resolve municipalities
 */
export function* saga_resolve_municipalities(action: saga_act): unknown {
  const municipalities: municipality_t[] = yield call(
    saga_do_request,
    http_resolve_municipalities,
    action
  )
  if (municipalities) {
    yield put(shoot.reduce_municipalities(municipalities))
  }
}

/**
 * Resolve localities
 */
export function* saga_resolve_localities(action: saga_act): unknown {
  const localities: locality_t[] = yield call(saga_do_request, http_resolve_localities, action)
  if (localities) {
    yield put(shoot.reduce_localities(localities))
  }
}

/**
 * Resolve postal codes
 */
export function* saga_resolve_caps(action: saga_act): unknown {
  const caps: cap_t[] = yield call(saga_do_request, http_resolve_caps, action)
  if (caps) {
    yield put(shoot.reduce_caps(caps))
  }
}

/**
 * Resolve toponyms
 */
export function* saga_resolve_toponyms(action: saga_act): unknown {
  const toponyms: district_t[] = yield call(saga_do_request, http_resolve_toponyms, action)
  if (toponyms) {
    yield put(shoot.reduce_toponyms(toponyms))
  }
}

/**
 * Resolve addresses
 */
export function* saga_resolve_addresses(action: saga_act): unknown {
  const addresses: addr_t[] = yield call(saga_do_request, http_resolve_addresses, action)
  if (addresses) {
    yield put(shoot.reduce_addresses(addresses))
  }
}

/**
 * Resolve associated customers
 */
export function* saga_resolve_associated_customers(action: saga_act): unknown {
  const customers: customer_t[] = yield call(
    saga_do_request,
    http_resolve_associated_customers,
    action
  )
  if (customers) {
    yield put(shoot.reduce_associated_customers(customers))
  }
}

/**
 * Check registration data
 */
export function* saga_check_registration_data(action: saga_act): unknown {
  const check_registration_data: check_registration_data_outcome_t = yield call(
    saga_do_request,
    http_check_registration_data,
    action
  )
  if (check_registration_data) {
    yield put(shoot.reduce_outcome_check_registration_data(check_registration_data))
  }
}

/**
 * Verify registration otp
 */
export function* saga_verify_registration_otp(action: saga_act): unknown {
  const verify_registration_otp: verify_registration_otp_outcome_t = yield call(
    saga_do_request,
    http_verify_registration_otp,
    action
  )
  if (verify_registration_otp) {
    yield put(shoot.reduce_outcome_verify_registration_otp(verify_registration_otp))
  }
}

/**
 * Check SEDA is not active on client mail
 * if active, it will block the flow showing a modal, if not active it will proceed with the requested action (update consents in current use case)
 */
export function* saga_resolve_check_seda(action: saga_act<{
    seda_check: seda_check_t;
    consentsTD: consent_t[];
  }>): unknown {
    try {
      const co: seda_check_outcome_t = yield call(
          saga_do_request,
          http_resolve_check_seda,
          { args: { ...action.args.seda_check }, type: action.type }
      );
  
      if (co.mandatoSeda !== undefined) {
        if (co.mandatoSeda === true) {
          // SEDA active on client mail -> show SEDA error modal (block flow)
          yield put(shoot.show_seda_error_modal('mail'));
        } else {
          // SEDA not active on client mail -> proceed with updating consents (per requested behavior)
          yield put(
            shoot.saga_update_consents({ proclie: action.args.seda_check.proclie, consents: action.args.consentsTD })
          );
        }
      } else {
        // Use the project's standard error reducer to surface a generic error
        yield put(shoot.reduce_error({ saga_type: action.type }));
      }
    } catch (error) {
      yield put(shoot.reduce_error({ saga_type: action.type }));
    }
}

/**
 * Check SEDA is not active on client pension fund
 * if active, it will block the flow showing a modal;
 * if not active it will check SEDA on client mail:
 * * if active it will block the flow showing a modal
 * * if not active it will proceed with the requested action (update consents in current use case)
 */
export function* saga_resolve_pension_fund_check_seda(action: saga_act<{
  consentsTD: consent_t[];
  seda_check?: seda_check_t;
}>): unknown {
  try {
      const co: seda_check_outcome_t = yield call(
          saga_do_request,
          http_resolve_check_pension_fund_seda,
          { args: {}, type: action.type }
      );
  
      if (co.mandatoSeda !== undefined) {
        if (co.mandatoSeda === true) {
          // SEDA on pension fund active -> show pension fund SEDA error modal (block flow)
          yield put(shoot.show_seda_error_modal('pension_fund'));
        } else {
          // SEDA on pension fund not active -> proceed with next check on mail SEDA
          yield put(
            shoot.saga_resolve_check_seda({ seda_check: action.args.seda_check, consentsTD: action.args.consentsTD })
          );
        }
      } else {
        // Use the project's standard error reducer to surface a generic error
        yield put(shoot.reduce_error({ saga_type: action.type }));
      }
    } catch (error) {
      yield put(shoot.reduce_error({ saga_type: action.type }));
    }
}
